import * as React from 'react';
import { createReactRenderer } from 'helpers/react-renderer';
import { Editor, IAllProps } from '@tinymce/tinymce-react';
// https://www.tiny.cloud/docs/advanced/usage-with-module-loaders/#es2015modules
// Import TinyMCE
import 'tinymce/tinymce';
// A theme is also required
import 'tinymce/themes/silver';
// Default icons are required for TinyMCE 5.3 or above
import 'tinymce/icons/default';
// Any plugins you want to use has to be imported
import 'tinymce/plugins/paste';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/lists';

// Use webpack's require.context to get the TinyMCE skin https://www.tiny.cloud/docs/advanced/usage-with-module-loaders/#webpackfile-loader
require.context(
  'file-loader?name=js/[path][name].[ext]&context=node_modules/tinymce!tinymce/skins',
  true,
  /.*/
);

interface Props {
  name: string;
  title: string;
  initialValue: string;
}

const decodeHTML = (html: string): string => {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
};

function isCharacterKeyPress(e: KeyboardEvent) {
  if (typeof e.which === 'undefined') {
    // This is for IE, which only fires keypress events for printable keys
    return true;
  }
  if (typeof e.which === 'number' && e.which > 0) {
    // In other browsers except old versions of WebKit, e.which is
    // only greater than zero if the keypress is a printable key.
    // We need to filter out backspace and ctrl/alt/meta key combinations
    return !e.ctrlKey && !e.metaKey && !e.altKey && e.which !== 8;
  }
  return false;
}

function getContentLength(text: string) {
  // strip out HTML tags to get the visible character length
  return text.replace(/(<([^>]+)>)/gi, '').length;
}

const handleKeyPress: IAllProps['onKeyPress'] = (event, editor) => {
  if (isCharacterKeyPress(event)) {
    // we only want to limit keypresses that are printable
    const maxLength = editor.settings.maxchars;
    // Current description content length minus html tags
    const currentLength = getContentLength(editor.getContent());

    // If the current description length is longer than maximum allowed,
    // undo the last input.
    if (currentLength >= maxLength) {
      event.preventDefault();
      event.stopPropagation();
    }
  }
};

const RichTextEditor: React.FunctionComponent<Props> = ({ name, title, initialValue = '' }) => {
  const editorRef = React.createRef<HTMLInputElement>();
  const [content, setContent] = React.useState(decodeHTML(initialValue));

  const handleEditorChange: IAllProps['onChange'] = newContent => {
    setContent(newContent);
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'dx' does not exist on type 'HTMLInputEle... Remove this comment to see the full error message
    if (editorRef.current && !editorRef.current?.dx) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'dx' does not exist on type 'HTMLInputEle... Remove this comment to see the full error message
      editorRef.current.dx = true;
    }
    window.viewFieldChanged('view');
  };

  return (
    <>
      <Editor
        value={content}
        init={{
          contextmenu: false,
          cleanup: true,
          maxchars: 12000,
          menubar: false,
          statusbar: false,
          convert_urls: false,
          valid_elements: 'a[href|target=_blank],em/i,strong/b,br,p,ol[start|reversed|type],ul,li',
          // For 'paste' plugin
          paste_preprocess: (_plugin: TSFixMe, args: TSFixMe) => {
            const tinymce = args.target;
            const maxCharsToPaste =
              tinymce.settings.maxchars - getContentLength(tinymce.getContent());

            // Paste as many characters as we can starting from the first character of the pasted text.
            // eslint-disable-next-line no-param-reassign
            args.content = args.content.substr(0, maxCharsToPaste);
          },
        }}
        onEditorChange={handleEditorChange}
        onKeyPress={handleKeyPress}
        toolbar="bold italic | bullist numlist | link"
        plugins="lists link image paste"
        textareaName="rich-text-editor"
      />
      <input
        ref={editorRef}
        id={`view_${name}`}
        name={name}
        title={title}
        className="view_view"
        value={content}
        type="hidden"
      />
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: RichTextEditor,
  selector: '[data-react-root="RichTextEditor"]',
});

let currentId: TSFixMe;
history.onChangeState(
  {
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'string[]' is not assignable to type 'string'... Remove this comment to see the full error message
    name: ['item.views.item', 'item_group.views.item_group'],
    tab: 'ecommerce',
    id: undefined,
  },
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'record_id' does not exist on type 'Histo... Remove this comment to see the full error message
  ({ record_id: id }) => {
    currentId = id;
    RenderedComponent.mount();
  },
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'name' does not exist on type 'HistorySta... Remove this comment to see the full error message
  ({ name, record_id: id }) => {
    if (name === 'item.views.item' && currentId === id) return;
    RenderedComponent.unmount();
  }
);

export { RichTextEditor };
